/**
 * BaseElement — common foundation for every theme custom element.
 *
 * Why: 60 customElements.define calls in this theme, only 5 use AbortController,
 * only 27 disconnectedCallback definitions. This class makes correct cleanup
 * the default, eliminating listener leaks in the Theme Editor and reducing
 * boilerplate by ~15 LOC per element.
 *
 * Usage:
 *   class MyEl extends BaseElement {
 *     connectedCallback() {
 *       this.on(this.button, 'click', () => this.handle());
 *       this.subscribe(theme.pubsub.EVENTS.cartUpdate, (data) => this.refresh(data));
 *     }
 *   }
 *   customElements.define('my-el', MyEl);
 *
 * Doc: docs/decisions/ADR-0001-base-custom-element.md
 */

window.theme = window.theme || {};

theme.BaseElement = class BaseElement extends HTMLElement {
  constructor() {
    super();
    this._abort = new AbortController();
    this._intervals = new Set();
    this._observers = new Set();
    this._unsubscribers = new Set();
  }

  /**
   * Default cleanup. Subclasses overriding disconnectedCallback MUST call super.
   */
  disconnectedCallback() {
    this._abort.abort();
    this._intervals.forEach((id) => clearInterval(id));
    this._intervals.clear();
    this._observers.forEach((o) => o.disconnect());
    this._observers.clear();
    this._unsubscribers.forEach((u) => {
      try {
        u();
      } catch {
        // A subscriber's unsubscribe throwing must not block the others.
      }
    });
    this._unsubscribers.clear();
  }

  /**
   * Adds an event listener that auto-removes on disconnect via AbortController.
   * @param {EventTarget} target
   * @param {string} type
   * @param {Function} handler
   * @param {AddEventListenerOptions} [opts]
   */
  on(target, type, handler, opts = {}) {
    if (!target) return;
    target.addEventListener(type, handler, { ...opts, signal: this._abort.signal });
  }

  /**
   * Subscribes to a theme.pubsub event. Auto-unsubscribes on disconnect.
   * @param {string} event
   * @param {Function} cb
   */
  subscribe(event, cb) {
    if (!theme.pubsub) {
      console.warn('theme.pubsub not loaded; subscribe() noop for', event);
      return;
    }
    const unsub = theme.pubsub.subscribe(event, cb);
    this._unsubscribers.add(unsub);
  }

  /**
   * Tracks a setInterval id for auto-clearing on disconnect.
   * @param {Function} fn
   * @param {number} ms
   * @returns {number}
   */
  setInterval(fn, ms) {
    const id = setInterval(fn, ms);
    this._intervals.add(id);
    return id;
  }

  /**
   * Tracks an Observer (Intersection/Mutation/Resize) for auto-disconnect.
   * @param {IntersectionObserver|MutationObserver|ResizeObserver} observer
   * @param {Element|Node} target
   * @param {object} [opts]
   * @returns the observer
   */
  observe(observer, target, opts) {
    this._observers.add(observer);
    if (target) observer.observe(target, opts);
    return observer;
  }

  /**
   * Dispatches a CustomEvent with the correct constructor signature.
   * Bubbles by default. Pass { bubbles: false } to opt out.
   * @param {string} type
   * @param {*} [detail]
   * @param {EventInit} [opts]
   */
  emit(type, detail, opts = {}) {
    this.dispatchEvent(new CustomEvent(type, { bubbles: true, ...opts, detail }));
  }

  /**
   * Traps focus inside this element (delegates to theme.utils.trapFocus).
   * The trap's cleanup function is tracked on this._abort.signal so that
   * leaving the element disconnected automatically releases focus too.
   * @param {HTMLElement} [focusEl] - optional initial focus target
   * @returns {Function} cleanup function
   */
  trapFocus(focusEl) {
    if (!theme.utils || !theme.utils.trapFocus) return () => {};
    const cleanup = theme.utils.trapFocus(this, focusEl);
    this._abort.signal.addEventListener('abort', cleanup, { once: true });
    return cleanup;
  }

  /**
   * Releases the focus trap previously installed via this.trapFocus().
   */
  releaseFocus() {
    if (theme.utils && theme.utils.releaseFocus) theme.utils.releaseFocus(this);
  }
};
